import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError } from 'axios';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';

import AlertModal from 'components/common/AlertModal';
import imageUploadModalService from 'components/common/ImageUploadModal/imageUploadModalService';
import patternAfterMealImage from 'features/MenuServingTime/images/pattern_after_meal.jpg';
import patternBeforeMealImage from 'features/MenuServingTime/images/pattern_before_meal.jpg';
import { useEffect, useState } from 'react';
import { Button, Form, Modal, Stack, Table } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError, IMAGE_SIZES } from 'utils';
import * as yup from 'yup';

import { useAppSelector } from 'app/hooks';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import ImageUploadControl from 'components/formfields/ImageUploadControl';
import { selectIsEditMode } from 'features/common/editModeSlice';
import {
  defaultValues,
  PROVISION_PATTERN_AFTER,
  PROVISION_PATTERN_BEFORE,
  PROVISION_PATTERN_TOGETHER,
  SERVING_TIME_AFTER_MEAL_IMAGE_JA,
  SERVING_TIME_FIRST_IMAGE_JA,
} from '../constant';
import { MenuServingTimeDetailItem, MenuServingTimeItemList } from '../model/MenuServingTime';
import menuServingTimeService from '../services';
import './style.scss';

type MenuServingTimeDetailProps = {
  menuServingTimeId: number | null;
  onClose: () => void;
  onUpdateNewItem: (newItem: MenuServingTimeItemList) => void;
};

function MenuServingTimeDetail(props: MenuServingTimeDetailProps) {
  const { menuServingTimeId, onClose, onUpdateNewItem } = props;
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();

  // yup setup
  const schema = yup.object({
    name: yup.string().required(t('menuServingTimeDetail.warn_field_required')),

    is_serving_time_first: yup.boolean(),
    is_serving_time_after_meal: yup.boolean(),
    is_serving_time_together: yup.boolean(),

    // validate require field when toggle turn ON
    serving_time_first_message_ja: yup.string().when('is_serving_time_first', {
      is: true,
      then: (schema) => schema.required(t('menuServingTimeDetail.warn_field_required')),
      otherwise: (schema) => schema.notRequired().nullable(), // not validate (allow null/undefined) field when toggle turn OFF
    }),
    serving_time_first_image_ja: yup.string().when('is_serving_time_first', {
      is: true,
      then: (schema) => schema.required(t('menuServingTimeDetail.warn_field_required')),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    serving_time_after_meal_message_ja: yup.string().when('is_serving_time_after_meal', {
      is: true,
      then: (schema) => schema.required(t('menuServingTimeDetail.warn_field_required')),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    serving_time_after_meal_image_ja: yup.string().when('is_serving_time_after_meal', {
      is: true,
      then: (schema) => schema.required(t('menuServingTimeDetail.warn_field_required')),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),

    serving_time_together_message_ja: yup.string().when('is_serving_time_together', {
      is: true,
      then: (schema) => schema.required(t('menuServingTimeDetail.warn_field_required')),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
    serving_time_together_image_ja: yup.string().when('is_serving_time_together', {
      is: true,
      then: (schema) => schema.required(t('menuServingTimeDetail.warn_field_required')),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  });

  // form setup
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    // getValues,
    formState: { errors },
  } = useForm<Partial<MenuServingTimeDetailItem>>({
    resolver: yupResolver(schema),
    defaultValues: defaultValues,
    mode: 'onSubmit',
  });

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  // state
  const [isLoading, setIsLoading] = useState(true);
  const [isCheckToggle, setCheckToggle] = useState(false);

  // watch
  const currentFormValues = watch();

  // load data from id (edit form)
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (menuServingTimeId) {
          const response = await menuServingTimeService.getItem(menuServingTimeId);
          if (response) {
            const menuServingTimeDetailItem: MenuServingTimeDetailItem = response.data;
            reset(menuServingTimeDetailItem);
          }
        }
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  if (isLoading) {
    return null;
  }

  const isDisableAllToggle =
    currentFormValues.is_serving_time_first === false &&
    currentFormValues.is_serving_time_after_meal === false &&
    currentFormValues.is_serving_time_together === false;

  function onSubmit(data: Partial<MenuServingTimeDetailItem>) {
    if (isDisableAllToggle) {
      showGlobalModal(() => (
        <AlertModal title={t('App.warning')} message={t('menuServingTimeDetail.provision_pattern_at_least_one')} />
      ));
      setCheckToggle(true);
      return;
    }
    if (menuServingTimeId) {
      //update data
      (async () => {
        try {
          const payload: Partial<MenuServingTimeDetailItem> = data;
          const response = await menuServingTimeService.updateItem(payload);
          if (response) {
            showGlobalModal(() => (
              <AlertModal title={t('App.info')} message={t('menuServingTimeDetail.sucesss_save')} onClose={onClose} />
            ));
            onUpdateNewItem(response.data);
          }
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    } else {
      // insert data
      (async () => {
        try {
          const payload: Partial<MenuServingTimeDetailItem> = { ...data };
          const key_first_image_jp = 'serving_time_first_image_ja';
          const key_after_image_jp = 'serving_time_after_meal_image_ja';
          // process upload default image
          const uploadDefaultImage = async (
            imageFieldName: keyof MenuServingTimeDetailItem,
            defaultImageUrl: string,
            expectedFilename: string
          ): Promise<void> => {
            const currentValue = payload[imageFieldName];

            if (currentValue === expectedFilename) {
              try {
                // fetch image from local URL
                const response = await fetch(defaultImageUrl);
                if (!response.ok) {
                  throw new Error(`Failed to fetch image from ${defaultImageUrl}: ${response.statusText}`);
                }
                // change to blob
                const imageBlob = await response.blob();
                // create File object from Blob
                const imageFile = new File([imageBlob], expectedFilename, { type: imageBlob.type });

                const bodyFormData = new FormData();
                bodyFormData.append('img_url', imageFile);

                const uploadResponse = await imageUploadModalService.uploadImage(bodyFormData);

                if (uploadResponse && uploadResponse.data && uploadResponse.data.filename) {
                  payload[imageFieldName] = uploadResponse.data.filename as any;
                }
              } catch (uploadError) {
                if (!handleServiceError(showGlobalModal, uploadError as AxiosError)) {
                  displayError(showGlobalModal, uploadError as AxiosError);
                }
              }
            }
          };

          await uploadDefaultImage(key_first_image_jp, patternBeforeMealImage, SERVING_TIME_FIRST_IMAGE_JA);
          await uploadDefaultImage(key_after_image_jp, patternAfterMealImage, SERVING_TIME_AFTER_MEAL_IMAGE_JA);

          const response = await menuServingTimeService.createItem(payload);
          if (response) {
            showGlobalModal(() => (
              <AlertModal title={t('App.info')} message={t('menuServingTimeDetail.sucesss_save')} onClose={onClose} />
            ));
            onUpdateNewItem(response.data);
          }
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  function renderTitle() {
    return (
      <>
        <tr>
          <th rowSpan={6} colSpan={1} style={{ width: '10%' }}>
            {t('menuServingTimeDetail.title')}
          </th>
          <th colSpan={3}>{t('menuServingTimeDetail.title_ja')}</th>
          <td colSpan={5}>
            <Form.Control className="text-center" {...register('title_ja')}></Form.Control>
          </td>
        </tr>
        <tr>
          <th colSpan={3}>{t('menuServingTimeDetail.title_en')}</th>
          <td colSpan={3}>
            <Form.Control className="text-center" {...register('title_en')}></Form.Control>
          </td>
        </tr>
        <tr>
          <th colSpan={3}>{t('menuServingTimeDetail.title_zh_hans')}</th>
          <td colSpan={3}>
            <Form.Control className="text-center" {...register('title_zh_hans')}></Form.Control>
          </td>
        </tr>
        <tr>
          <th colSpan={3}>{t('menuServingTimeDetail.title_zh_hant')}</th>
          <td colSpan={3}>
            <Form.Control className="text-center" {...register('title_zh_hant')}></Form.Control>
          </td>
        </tr>
        <tr>
          <th colSpan={3}>{t('menuServingTimeDetail.title_ko')}</th>
          <td colSpan={3}>
            <Form.Control className="text-center" {...register('title_ko')}></Form.Control>
          </td>
        </tr>
        <tr>
          <th colSpan={3}>{t('menuServingTimeDetail.title_vi')}</th>
          <td colSpan={3}>
            <Form.Control className="text-center" {...register('title_vi')}></Form.Control>
          </td>
        </tr>
      </>
    );
  }

  const calculateModuleRowSpan = (isEnabled: boolean) => {
    // 1 row toggle enable/disable
    // 6 row message (if enabled)
    // 6 row image (if enabled)
    return isEnabled ? 1 + 6 + 6 : 1; // 13 row if enabled, else 1 row only for toggle
  };

  function renderProvisionPattern() {
    const currentFormValues = watch();
    if (currentFormValues) {
      const beforeRowSpan = calculateModuleRowSpan(currentFormValues.is_serving_time_first ?? true);
      const afterRowSpan = calculateModuleRowSpan(currentFormValues.is_serving_time_after_meal ?? true);
      const togetherRowSpan = calculateModuleRowSpan(currentFormValues.is_serving_time_together ?? false);
      const totalProvisionPatternRowSpan = beforeRowSpan + afterRowSpan + togetherRowSpan + 4;

      return (
        <>
          <tr>
            <th rowSpan={totalProvisionPatternRowSpan} colSpan={1}>
              {t('menuServingTimeDetail.provision_pattern')}
            </th>
          </tr>
          <tr>
            <th rowSpan={beforeRowSpan + 1} colSpan={1} style={{ width: '7%' }}>
              {t('menuServingTimeDetail.before')}
            </th>
          </tr>
          {renderProvisionPatternModule(PROVISION_PATTERN_BEFORE)}
          <tr>
            <th rowSpan={afterRowSpan + 1} colSpan={1}>
              {t('menuServingTimeDetail.after')}
            </th>
          </tr>
          {renderProvisionPatternModule(PROVISION_PATTERN_AFTER)}
          <tr>
            <th rowSpan={togetherRowSpan + 1} colSpan={1}>
              {t('menuServingTimeDetail.together')}
            </th>
          </tr>
          {renderProvisionPatternModule(PROVISION_PATTERN_TOGETHER)}
        </>
      );
    }
  }

  function renderProvisionPatternModule(type: String) {
    const currentFormValues = watch();
    let isEnabled: boolean;

    switch (type) {
      case PROVISION_PATTERN_BEFORE:
        isEnabled = currentFormValues.is_serving_time_first ?? true;
        break;
      case PROVISION_PATTERN_AFTER:
        isEnabled = currentFormValues.is_serving_time_after_meal ?? true;
        break;
      case PROVISION_PATTERN_TOGETHER:
        isEnabled = currentFormValues.is_serving_time_together ?? false;
        break;
      default:
        isEnabled = false;
    }

    const getMessageRegisterName = (lang: string) => {
      switch (type) {
        case PROVISION_PATTERN_BEFORE:
          return `serving_time_first_message_${lang}` as keyof Partial<MenuServingTimeDetailItem>;
        case PROVISION_PATTERN_AFTER:
          return `serving_time_after_meal_message_${lang}` as keyof Partial<MenuServingTimeDetailItem>;
        case PROVISION_PATTERN_TOGETHER:
          return `serving_time_together_message_${lang}` as keyof Partial<MenuServingTimeDetailItem>;
        default:
          return 'name';
      }
    };

    const getImageRegisterName = (lang: string) => {
      switch (type) {
        case PROVISION_PATTERN_BEFORE:
          return `serving_time_first_image_${lang}` as keyof Partial<MenuServingTimeDetailItem>;
        case PROVISION_PATTERN_AFTER:
          return `serving_time_after_meal_image_${lang}` as keyof Partial<MenuServingTimeDetailItem>;
        case PROVISION_PATTERN_TOGETHER:
          return `serving_time_together_image_${lang}` as keyof Partial<MenuServingTimeDetailItem>;
        default:
          return 'name';
      }
    };

    const handleToggleButton = (e: React.ChangeEvent<HTMLInputElement>, type: String) => {
      const newCheckedStatus = e.target.checked;
      // const currentFormValues = getValues();

      let fieldToUpdate: keyof Partial<MenuServingTimeDetailItem> | null = null;
      // let currentIsServerFirst = currentFormValues.is_serving_time_first;
      // let currentIsServerAfterMeal = currentFormValues.is_serving_time_after_meal;
      // let currentIsServerTogether = currentFormValues.is_serving_time_together;

      if (type === PROVISION_PATTERN_BEFORE) {
        fieldToUpdate = 'is_serving_time_first';
      } else if (type === PROVISION_PATTERN_AFTER) {
        fieldToUpdate = 'is_serving_time_after_meal';
      } else if (type === PROVISION_PATTERN_TOGETHER) {
        fieldToUpdate = 'is_serving_time_together';
      }

      if (!fieldToUpdate) {
        console.error('Unknown toggle type:', type);
        return;
      }

      // ensure one of the toggles is always ON
      // if (!newCheckedStatus) {
      //   let activeTogglesCount = 0;

      //   if (fieldToUpdate !== 'is_serving_time_first' && currentIsServerFirst) activeTogglesCount++;
      //   if (fieldToUpdate !== 'is_serving_time_after_meal' && currentIsServerAfterMeal) activeTogglesCount++;
      //   if (fieldToUpdate !== 'is_serving_time_together' && currentIsServerTogether) activeTogglesCount++;

      //   if (activeTogglesCount === 0) {
      //     setValue(fieldToUpdate, true);
      //     return;
      //   }
      // }

      setValue(fieldToUpdate, newCheckedStatus);
      setCheckToggle(false);
    };

    const currentMessageJaRegisterName = getMessageRegisterName('ja');
    const currentMessageJaError = errors[currentMessageJaRegisterName];

    const currentImageJaRegisterName = getImageRegisterName('ja');
    const currentImageJaError = errors[currentImageJaRegisterName];

    const renderEmptyImageBox = (imageValue: string) => {
      if (!imageValue) {
        return (
          <div
            style={{
              width: '150px',
              minHeight: '30px',
              color: 'black',
              border: '1.5px solid #82B366',
              borderColor: '#82B366',
              backgroundColor: '#D5E8D4',
              zIndex: 1,
              position: 'absolute',
            }}
            className="d-flex align-items-center justify-content-center"
          >
            {t('menuServingTimeDetail.image')}
          </div>
        );
      }
      return null;
    };

    return (
      <>
        <tr>
          <th colSpan={2}>{t('menuServingTimeDetail.enable_disable')}</th>
          <td colSpan={4}>
            <Form.Check
              type="switch"
              style={{ color: '#38D145' }}
              id={`provision-pattern-switch-${type}`}
              className="custom-menu-serving-time-status-switch"
              checked={isEnabled}
              onChange={(e) => handleToggleButton(e, type)}
              isInvalid={isCheckToggle && type === PROVISION_PATTERN_BEFORE}
            ></Form.Check>
            {isCheckToggle && type === PROVISION_PATTERN_BEFORE && (
              <small className="text-danger d-inline-block mt-1">
                {t('menuServingTimeDetail.warn_field_required')}
              </small>
            )}
          </td>
        </tr>

        {/* MESSAGE SECTION */}
        {isEnabled && (
          <>
            <tr>
              <th rowSpan={6} colSpan={1} style={{ width: '8%' }}>
                {t('menuServingTimeDetail.message')}
              </th>
              <th style={{ width: '12%' }}>{t('menuServingTimeDetail.title_ja')}</th>
              <td colSpan={3}>
                <Form.Control className="text-center" {...register(getMessageRegisterName('ja'))}></Form.Control>
                {currentMessageJaError && (
                  <small className="text-danger d-inline-block mt-1">{currentMessageJaError.message}</small>
                )}
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_en')}</th>
              <td colSpan={3}>
                <Form.Control className="text-center" {...register(getMessageRegisterName('en'))}></Form.Control>
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_zh_hans')}</th>
              <td colSpan={3}>
                <Form.Control className="text-center" {...register(getMessageRegisterName('zh_hans'))}></Form.Control>
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_zh_hant')}</th>
              <td colSpan={3}>
                <Form.Control className="text-center" {...register(getMessageRegisterName('zh_hant'))}></Form.Control>
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_ko')}</th>
              <td colSpan={3}>
                <Form.Control className="text-center" {...register(getMessageRegisterName('ko'))}></Form.Control>
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_vi')}</th>
              <td colSpan={3}>
                <Form.Control className="text-center" {...register(getMessageRegisterName('vi'))}></Form.Control>
              </td>
            </tr>
          </>
        )}

        {/* IMAGE SECTION */}
        {isEnabled && (
          <>
            <tr>
              <th rowSpan={6} colSpan={1}>
                {t('menuServingTimeDetail.image')}
              </th>
              <th>{t('menuServingTimeDetail.title_ja')}</th>
              <td colSpan={3}>
                <Stack style={{ position: 'relative' }}>
                  {renderEmptyImageBox(currentFormValues[getImageRegisterName('ja')] as string)}
                  <ImageUploadControl
                    name={getImageRegisterName('ja')}
                    minWidth={IMAGE_SIZES.menu_serving_time_image.minWidth}
                    minHeight={IMAGE_SIZES.menu_serving_time_image.minHeight}
                    maxWidth={IMAGE_SIZES.menu_serving_time_image.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_serving_time_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_serving_time_image.maxFileSize}
                    control={control}
                  ></ImageUploadControl>
                </Stack>
                {currentImageJaError && (
                  <small className="text-danger d-inline-block mt-1">{currentImageJaError.message}</small>
                )}
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_en')}</th>
              <td colSpan={3}>
                <Stack style={{ position: 'relative' }}>
                  {renderEmptyImageBox(currentFormValues[getImageRegisterName('en')] as string)}
                  <ImageUploadControl
                    name={getImageRegisterName('en')}
                    minWidth={IMAGE_SIZES.menu_serving_time_image.minWidth}
                    minHeight={IMAGE_SIZES.menu_serving_time_image.minHeight}
                    maxWidth={IMAGE_SIZES.menu_serving_time_image.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_serving_time_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_serving_time_image.maxFileSize}
                    control={control}
                  ></ImageUploadControl>
                </Stack>
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_zh_hans')}</th>
              <td colSpan={3}>
                <Stack style={{ position: 'relative' }}>
                  {renderEmptyImageBox(currentFormValues[getImageRegisterName('zh_hans')] as string)}
                  <ImageUploadControl
                    name={getImageRegisterName('zh_hans')}
                    minWidth={IMAGE_SIZES.menu_serving_time_image.minWidth}
                    minHeight={IMAGE_SIZES.menu_serving_time_image.minHeight}
                    maxWidth={IMAGE_SIZES.menu_serving_time_image.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_serving_time_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_serving_time_image.maxFileSize}
                    control={control}
                  ></ImageUploadControl>
                </Stack>
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_zh_hant')}</th>
              <td colSpan={3}>
                <Stack style={{ position: 'relative' }}>
                  {renderEmptyImageBox(currentFormValues[getImageRegisterName('zh_hant')] as string)}
                  <ImageUploadControl
                    name={getImageRegisterName('zh_hant')}
                    minWidth={IMAGE_SIZES.menu_serving_time_image.minWidth}
                    minHeight={IMAGE_SIZES.menu_serving_time_image.minHeight}
                    maxWidth={IMAGE_SIZES.menu_serving_time_image.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_serving_time_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_serving_time_image.maxFileSize}
                    control={control}
                  ></ImageUploadControl>
                </Stack>
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_ko')}</th>
              <td colSpan={3}>
                <Stack style={{ position: 'relative' }}>
                  {renderEmptyImageBox(currentFormValues[getImageRegisterName('ko')] as string)}
                  <ImageUploadControl
                    name={getImageRegisterName('ko')}
                    minWidth={IMAGE_SIZES.menu_serving_time_image.minWidth}
                    minHeight={IMAGE_SIZES.menu_serving_time_image.minHeight}
                    maxWidth={IMAGE_SIZES.menu_serving_time_image.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_serving_time_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_serving_time_image.maxFileSize}
                    control={control}
                  ></ImageUploadControl>
                </Stack>
              </td>
            </tr>
            <tr>
              <th>{t('menuServingTimeDetail.title_vi')}</th>
              <td colSpan={3}>
                <Stack style={{ position: 'relative' }}>
                  {renderEmptyImageBox(currentFormValues[getImageRegisterName('vi')] as string)}
                  <ImageUploadControl
                    name={getImageRegisterName('vi')}
                    minWidth={IMAGE_SIZES.menu_serving_time_image.minWidth}
                    minHeight={IMAGE_SIZES.menu_serving_time_image.minHeight}
                    maxWidth={IMAGE_SIZES.menu_serving_time_image.maxWidth}
                    maxHeight={IMAGE_SIZES.menu_serving_time_image.maxHeight}
                    maxFileSize={IMAGE_SIZES.menu_serving_time_image.maxFileSize}
                    control={control}
                  ></ImageUploadControl>
                </Stack>
              </td>
            </tr>
          </>
        )}
      </>
    );
  }

  return (
    <Modal show dialogClassName="menuservingtime-detail" backdrop="static" onHide={onClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('menuServingTimeDetail.subject')} />
      </Modal.Header>
      <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="d-flex justify-content-end my-3">
            <div className="ps-3">
              <Button type="submit" className="btn btn--dark-green">
                {t('menuServingTimeDetail.button.save')}
              </Button>
            </div>
          </div>

          {currentFormValues && (
            <Table bordered className="form-menuservingtime">
              <tbody>
                {/* Name */}
                <tr>
                  <th colSpan={4}>{t('menuServingTimeDetail.name')}</th>
                  <td colSpan={4}>
                    <Form.Control className="text-center" {...register('name')}></Form.Control>
                    {errors.name && <small className="text-danger d-inline-block mt-1">{errors.name.message}</small>}
                  </td>
                </tr>
                {/* Title */}
                {renderTitle()}
                {/* Provision Pattern */}
                {renderProvisionPattern()}
                {/* Note */}
                <tr>
                  <th colSpan={4}>{t('menuServingTimeDetail.note')}</th>
                  <td colSpan={4}>
                    <Form.Control as="textarea" rows={6} {...register('note')}></Form.Control>
                  </td>
                </tr>
              </tbody>
            </Table>
          )}
        </form>
      </Modal.Body>
    </Modal>
  );
}
export default MenuServingTimeDetail;
